// src/components/AddProfile.jsx
import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Typography,
  CssBaseline,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  TextField,
  Snackbar,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Brightness4, Brightness7, Logout, Menu as MenuIcon } from "@mui/icons-material";
import { ThemeContext } from "../ThemeContext";
import { Link } from "react-router-dom";
import API, { checkAvatar } from "../API"; // Импортируем функцию checkAvatar

function AddProfile() {
  const { darkMode, toggleTheme } = useContext(ThemeContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [name, setName] = useState("");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const isMobile = useMediaQuery("(max-width: 1800px)");

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      background: {
        default: darkMode ? "#0d1117" : "#f9f9f9",
      },
      text: {
        primary: darkMode ? "#c9d1d9" : "#000000",
        secondary: darkMode ? "#8b949e" : "#5f6368",
      },
    },
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/";
    } else {
      fetchProfiles();
    }
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await API.get("/profiles/");
      setProfiles(response.data);
    } catch (error) {
      console.error("Ошибка при получении профилей:", error.response?.data || error);
    }
  };

  const handleDeleteProfile = async (id) => {
    try {
      await API.delete(`/profiles/${id}`);
      setProfiles(profiles.filter((profile) => profile.id !== id));
      setSnackbar({
        open: true,
        message: "Профиль успешно удален!",
        type: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Не удалось удалить профиль. Попробуйте снова.",
        type: "error",
      });
    }
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (name.trim().length < 3) {
      setSnackbar({
        open: true,
        message: "Имя должно содержать не менее 3 символов.",
        type: "error",
      });
      return;
    }
    if (login.trim().length < 3) {
      setSnackbar({
        open: true,
        message: "Логин должен содержать не менее 3 символов.",
        type: "error",
      });
      return;
    }
    if (password.trim().length < 8) {
      setSnackbar({
        open: true,
        message: "Пароль должен содержать не менее 8 символов.",
        type: "error",
      });
      return;
    }

    const profileData = { name, login, password };

    try {
      const response = await API.post("/profiles/", profileData);
      setProfiles([...profiles, response.data]);
      setSnackbar({
        open: true,
        message: "Профиль успешно добавлен!",
        type: "success",
      });
      setName("");
      setLogin("");
      setPassword("");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Не удалось добавить профиль. Попробуйте снова.";
      setSnackbar({ open: true, message: errorMessage, type: "error" });
    }
  };

  const ProfileCardComponent = ({ profile }) => {
    const defaultAvatar = "https://srv-backend-q7f9x1.firetolove.fun/static/images.jpeg";
    const [avatarUrl, setAvatarUrl] = useState(defaultAvatar);

    useEffect(() => {
      const fetchAvatar = async () => {
        if (profile.avatar_hash) {
          const url = await checkAvatar(profile.avatar_hash);
          setAvatarUrl(url);
        } else {
          setAvatarUrl(defaultAvatar);
        }
      };
      fetchAvatar();
    }, [profile.avatar_hash]);

    return (
      <Box
        sx={{
          width: "300px",
          padding: "1rem",
          borderRadius: "15px",
          backgroundColor: darkMode ? "#161b22" : "#ffffff",
          boxShadow: darkMode
            ? "0 5px 20px rgba(0, 0, 0, 0.6)"
            : "0 5px 20px rgba(0, 0, 0, 0.1)",
          margin: "0.5rem", // Уменьшено расстояние между карточками
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "200px",
            borderRadius: "15px",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: darkMode ? "#161b22" : "#f9f9f9",
          }}
        >
          <img
            src={avatarUrl}
            alt="Avatar"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = defaultAvatar;
            }}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </Box>

        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: "bold",
            textAlign: "center",
            margin: "0.5rem 0",
          }}
        >
          {profile.name}
        </Typography>
        <Button
          variant="contained"
          color="error"
          sx={{
            marginTop: "0.5rem",
            borderRadius: "8px",
          }}
          onClick={() => handleDeleteProfile(profile.id)}
        >
          Удалить
        </Button>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.type}
          sx={{
            width: "100%",
            backgroundColor:
              snackbar.type === "success" ? "#238636" : "#d32f2f",
            color: "#fff",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
        }}
      >
        <IconButton onClick={toggleTheme} color="inherit">
          {darkMode ? <Brightness7 /> : <Brightness4 />}
        </IconButton>
      </Box>

      {isMobile && (
        <Box
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
          }}
        >
          <IconButton onClick={toggleMenu} color="inherit">
            <MenuIcon />
          </IconButton>
        </Box>
      )}

      {/* Drawer for mobile */}
      <Drawer
        anchor="left"
        open={menuOpen} // Menu is controlled by the menuOpen state
        onClose={toggleMenu}
        sx={{
          "& .MuiDrawer-paper": {
            width: "240px",
            backgroundColor: darkMode ? "#0d1117" : "#f9f9f9",
            color: theme.palette.text.primary,
          },
        }}
      >
        <List>
          <ListItem button component={Link} to="/Home_menu">
            <ListItemText primary="Домой" />
          </ListItem>
          <ListItem button component={Link} to="/add-profile">
            <ListItemText primary="Добавить профайл" />
          </ListItem>
          <ListItem button component={Link} to="/Bot">
            <ListItemText primary="Бот" />
          </ListItem>
          <ListItem button onClick={handleLogout}>
            <Logout />
            <ListItemText primary="Выход" />
          </ListItem>
        </List>
      </Drawer>

      {/* Permanent Drawer for larger screens */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          anchor="left"
          sx={{
            "& .MuiDrawer-paper": {
              width: "240px",
              backgroundColor: darkMode ? "#0d1117" : "#f9f9f9",
              color: theme.palette.text.primary,
            },
          }}
        >
          <List>
            <ListItem button component={Link} to="/Home_menu">
              <ListItemText primary="Домой" />
            </ListItem>
            <ListItem button component={Link} to="/add-profile">
              <ListItemText primary="Добавить профайл" />
            </ListItem>
            <ListItem button component={Link} to="/Bot">
              <ListItemText primary="Бот" />
            </ListItem>
            <ListItem button onClick={handleLogout}>
              <Logout />
              <ListItemText primary="Выход" />
            </ListItem>
          </List>
        </Drawer>
      )}

      <Box
        sx={{
          width: "70vw",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          borderRadius: "8px",
          margin: "0 auto",
          padding: "2rem",
          minHeight: "100vh",
          background: darkMode
            ? "radial-gradient(circle, #0d1117 0%, #161b22 100%)"
            : "radial-gradient(circle, #f9f9f9 0%, #ffffff 100%)",
          boxShadow: darkMode
            ? "0 4px 20px rgba(0, 0, 0, 0.8)"
            : "0 4px 20px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Форма добавления нового профиля */}
        <Box
          sx={{
            width: "300px",
            padding: "1rem",
            borderRadius: "15px",
            backgroundColor: darkMode ? "#161b22" : "#ffffff",
            boxShadow: darkMode
              ? "0 5px 20px rgba(0, 0, 0, 0.6)"
              : "0 5px 20px rgba(0, 0, 0, 0.1)",
            margin: "1rem",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              marginBottom: "1rem",
              color: theme.palette.text.primary,
              textAlign: "center",
            }}
          >
            Добавить новый профиль
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
            }}
          >
            <TextField
              label="Имя"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Логин"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Пароль"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
            >
              Добавить
            </Button>
          </Box>
        </Box>

        {/* Карточки профилей */}
        {profiles.map((profile) => (
          <ProfileCardComponent key={profile.id} profile={profile} />
        ))}
      </Box>
    </ThemeProvider>
  );
}

export default AddProfile;
